import { memo } from "react";
import { Box, Typography, Card, CardContent, Badge } from "@mui/material";
import Grid from "@mui/material/Grid2";
import AttachmentIcon from "@mui/icons-material/Attachment";
import ImageIcon from "@mui/icons-material/Image";
import ImageCarousel from "./ImageCarousel";
import ModernCard from "./ModernCard";
import PropTypes from "prop-types";

const FilesTable = memo(({ attachments, formatCreatedAt, onFileClick, t }) => (
  <ModernCard
    title={t("documentDetail.filesTitle")}
    icon={AttachmentIcon}
    color="secondary"
  >
    {attachments.length > 0 ? (
      (() => {
        const images = attachments.filter((file) => file.is_image);
        const documents = attachments.filter((file) => !file.is_image);

        return (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 4,
            }}
          >
            {/* Image Carousel Section */}
            {images.length > 0 && (
              <Box>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1.5,
                    mb: 2,
                  }}
                >
                  <ImageIcon
                    sx={{
                      color: "info.main",
                      fontSize: { xs: 16, sm: 18 },
                    }}
                  />
                  <Typography
                    variant="subtitle1"
                    sx={{
                      fontWeight: 600,
                      fontSize: { xs: "0.9rem", sm: "1rem" },
                    }}
                  >
                    {t("documentDetail.imagesTitle", "Images")}
                  </Typography>
                  <Badge
                    badgeContent={images.length}
                    color="info"
                    sx={{
                      "& .MuiBadge-badge": {
                        fontSize: "0.75rem",
                        fontWeight: 600,
                        minWidth: 20,
                        height: 20,
                      },
                    }}
                  >
                    <Box />
                  </Badge>
                </Box>
                <ImageCarousel
                  images={images}
                  onImageClick={onFileClick}
                  convertUTCToLocal={formatCreatedAt}
                  t={t}
                />
              </Box>
            )}

            {/* Documents Grid Section */}
            {documents.length > 0 && (
              <Box>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1.5,
                    mb: 2,
                  }}
                >
                  <AttachmentIcon
                    sx={{
                      color: "secondary.main",
                      fontSize: { xs: 16, sm: 18 },
                    }}
                  />
                  <Typography
                    variant="subtitle1"
                    sx={{
                      fontWeight: 600,
                      fontSize: { xs: "0.9rem", sm: "1rem" },
                    }}
                  >
                    {t("documentDetail.documentsTitle", "Documents")}
                  </Typography>
                  <Badge
                    badgeContent={documents.length}
                    color="secondary"
                    sx={{
                      "& .MuiBadge-badge": {
                        fontSize: "0.75rem",
                        fontWeight: 600,
                        minWidth: 20,
                        height: 20,
                      },
                    }}
                  >
                    <Box />
                  </Badge>
                </Box>
                <Grid container spacing={2}>
                  {documents.map((file) => (
                    <Grid key={file.id} size={{ xs: 12, sm: 6, md: 4 }}>
                      <Card
                        variant="outlined"
                        sx={{
                          cursor: "pointer",
                          borderRadius: 2,
                          transition: "all 0.2s ease-in-out",
                          "&:hover": {
                            boxShadow: 4,
                            transform: "translateY(-4px)",
                            borderColor: "primary.main",
                          },
                        }}
                        onClick={() => onFileClick(file.url)}
                      >
                        <CardContent sx={{ textAlign: "center", p: 2 }}>
                          <Box sx={{ py: 2 }}>
                            <AttachmentIcon
                              sx={{
                                fontSize: 48,
                                color: "text.secondary",
                                mb: 1,
                              }}
                            />
                          </Box>
                          <Typography variant="body2" fontWeight="bold" noWrap>
                            {file.display_name || t("documentDetail.fileLabel")}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {formatCreatedAt(file.created_at)}
                          </Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              </Box>
            )}
          </Box>
        );
      })()
    ) : (
      <Box sx={{ textAlign: "center", py: 4 }}>
        <AttachmentIcon sx={{ fontSize: 48, color: "text.disabled", mb: 2 }} />
        <Typography variant="body2" color="text.secondary">
          {t("documentDetail.noFilesAvailable")}
        </Typography>
      </Box>
    )}
  </ModernCard>
));

FilesTable.displayName = "FilesTable";

FilesTable.propTypes = {
  attachments: PropTypes.array.isRequired,
  formatCreatedAt: PropTypes.func.isRequired,
  onFileClick: PropTypes.func.isRequired,
  t: PropTypes.func.isRequired,
};

export default FilesTable;
